// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Spinner } from '@loa/ui-components';
import openBankingApi from '../../api/openBankingApi';
import type { BankTransaction } from '../../types/openBanking';
import './BankTransactionsTable.css';

interface BankTransactionsTableProps {
  userId: string;
  accountId: string;
  currencyCode: string;
  /** Initial page size; user can paginate further. */
  pageSize?: number;
}

/**
 * Paginated transactions for one account. Loads on mount + on page
 * change. Shows date / type / description / details / amount columns;
 * positive amounts render green (income), negative red (outflow).
 *
 * <p>The server's {@code /transactions} endpoint defaults to a 6-month
 * window; we don't pass {@code fromDate}/{@code toDate} so we get that
 * default. Filter UI is a follow-up.
 */
export function BankTransactionsTable({
  userId,
  accountId,
  currencyCode,
  pageSize = 25,
}: BankTransactionsTableProps) {
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    openBankingApi.getTransactions(userId, accountId, { pageNumber: page, pageSize })
      .then((res) => {
        if (cancelled) return;
        setTransactions(res.content ?? []);
        // Server doesn't reliably return totalPages; infer hasMore from
        // a full page returned.
        setHasMore((res.content ?? []).length === pageSize);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, accountId, page, pageSize]);

  if (loading && transactions.length === 0) {
    return (
      <div className="bank-transactions-table__loading">
        <Spinner size="md" text="Loading transactions..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bank-transactions-table__error">
        Couldn't load transactions: {error.message}
      </div>
    );
  }

  if (transactions.length === 0) {
    return <div className="bank-transactions-table__empty">No transactions in the last 6 months.</div>;
  }

  return (
    <div className="bank-transactions-table">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Description</th>
            <th>Details</th>
            <th className="bank-transactions-table__amount-col">Amount</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((t) => (
            <tr key={t.id}>
              <td>{formatDate(t.date)}</td>
              <td><span className="bank-transactions-table__type">{t.type}</span></td>
              <td>{t.description}</td>
              <td className="bank-transactions-table__details">{t.details ?? ''}</td>
              <td className={`bank-transactions-table__amount-col ${t.amount < 0 ? 'bank-transactions-table__amount--out' : 'bank-transactions-table__amount--in'}`}>
                {formatAmount(t.amount, t.currencyCode || currencyCode)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="bank-transactions-table__pager">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0 || loading}
        >
          ← Previous
        </button>
        <span className="bank-transactions-table__pager-label">Page {page + 1}</span>
        <button
          type="button"
          onClick={() => setPage((p) => p + 1)}
          disabled={!hasMore || loading}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 2,
      signDisplay: 'always',
    }).format(amount);
  } catch {
    return `${amount > 0 ? '+' : ''}${amount.toFixed(2)} ${currency}`;
  }
}
